<template>
  <TagList :tags="tags" :is-small="true" />
</template>

<script setup lang="ts">
import type { TagInterface } from '@hypercodestudio/basler-components/dist/components/elements/taglist/TagList.vue';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import { TagList } from '@hypercodestudio/basler-components';
import type { ProductAggregationFragment } from '~/lib/Shop/generated/schema';
import { formatMoney } from '~/utils/shop/formatMoney';
import { useProductFilterStore } from '~/stores/productFilterStore/productFilterStore';

export interface FilterTag {
  label: string;
  code: string;
  values: string[];
}

type IndexedProductAggregations = Record<string, ProductAggregationFragment>;

const store = useProductFilterStore();
const { $textDictionary, $locale } = useNuxtApp();

function buildTag(filterTag: FilterTag): TagInterface {
  return {
    text: filterTag.label,
    element: 'button',
    showCloseIcon: true,
    link: {
      uri: store.createNewUrlParamsFromFilter(filterTag.code, filterTag.values)
    } satisfies HyperlinkInterface
  };
}

function buildFilterTag(
  label: string,
  code: string,
  values: string[]
): FilterTag {
  return { label, code, values };
}

function getIndexedProductAggregations(
  aggregations: ProductAggregationFragment[]
) {
  return aggregations.reduce<IndexedProductAggregations>((prev, curr) => {
    if (curr.attribute_code) {
      return { ...prev, [curr.attribute_code]: curr };
    }
    return { ...prev };
  }, {});
}

/** TODO: add: visible filters */
const tags = computed(() => {
  const indexedAggregations = getIndexedProductAggregations(store.aggregations);
  const validFilters: Record<string, string[]> = {};
  Object.entries(store.visibleFilters).forEach(([key, value]) => {
    if (indexedAggregations[key]) {
      validFilters[key] = value;
    }
  });

  return Object.entries(validFilters)
    .filter(([_, values]) => values != null && values.length > 0)
    .flatMap(([key, values]) =>
      extractFilterTag(store.aggregations, indexedAggregations, key, values)
    )
    .map((filterTag) => {
      return buildTag(filterTag);
    });
});

function extractFilterTag(
  aggregations: ProductAggregationFragment[],
  indexedAggregations: IndexedProductAggregations,
  code: string,
  values: string[]
) {
  const filterLabel = indexedAggregations[code]?.label
    ? `${indexedAggregations[code].label}: `
    : '';

  if (code.includes('_range') && Array.isArray(values) && values.length === 2) {
    const from = values[0];
    const to = values[1];
    const label = `${filterLabel}${from} ${
      $textDictionary.value[`productsList.filter.tags.${code}.label`]
    } - ${to} ${
      $textDictionary.value[`productsList.filter.tags.${code}.label`]
    }`;

    return [buildFilterTag(label, code, values)];
  }

  if (
    (code === 'sensor_pixelsize_horizontal_range' ||
      code === 'sensor_pixelsize_vertical_range') &&
    values.length === 2
  ) {
    const from = values[0];
    const to = values[1];
    const label = `${filterLabel}${from} ${$textDictionary.value['productsList.filter.tags.micrometer.label']} - ${to} ${$textDictionary.value['productsList.filter.tags.micrometer.label']}`;
    return [buildFilterTag(label, code, values)];
  }

  if (code === 'price' && values.length === 2) {
    const from = formatMoney(
      {
        currency: store.currencyCode,
        value: parseInt(values[0])
      },
      $locale.value!
    );

    const to = formatMoney(
      {
        currency: store.currencyCode,
        value: parseInt(values[1])
      },
      $locale.value!
    );
    const label = `${filterLabel}${from} - ${to}`;
    return [buildFilterTag(label, code, values)];
  }

  const aggregationTags =
    aggregations
      .find(
        (aggregation) =>
          aggregation.attribute_code === code && aggregation.options
      )
      ?.options?.filter((option) => option && values.includes(option.value))
      .map<FilterTag>((option) =>
        buildFilterTag(`${filterLabel}${option!.label ?? ''}`, code, [
          option!.value
        ])
      ) ?? [];

  if (aggregationTags.length) {
    return aggregationTags;
  }

  return values.map((value) => buildFilterTag(value, code, [value]));
}
</script>
